/**
 * ReportIssueWidget — In-app issue reporting for radiologists.
 *
 * Floating action button (FAB) that opens a modal where the user can:
 *  - Select a category (Transcription, CPT, Report, Intelligence, UI/UX, Other)
 *  - Select a severity (Critical / High / Medium / Low)
 *  - Write a free-text description
 *  - Capture a screenshot with optional annotation (draw on canvas)
 *  - Submit — evidence bundle (console, network, Redux) is auto-collected
 *
 * PHI masking is applied to all evidence before submission.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import BrushIcon from '@mui/icons-material/Brush';
import UndoIcon from '@mui/icons-material/Undo';
import SendIcon from '@mui/icons-material/Send';
import ShieldIcon from '@mui/icons-material/Shield';

import evidenceCollectorService, {
  type EvidenceBundle,
} from '../services/evidenceCollectorService';
import issueReportService, {
  type IssueCategory,
  type IssueSeverity,
} from '../services/issueReportService';
import { maskString } from '../utils/phiMasking';
import { maskScreenshotPhi, type MaskResult } from '../utils/screenshotPhiMasker';
import { maskDomPhi, type DomMaskResult } from '../utils/domPhiMasker';

const CATEGORIES: { value: IssueCategory; label: string }[] = [
  { value: 'transcription', label: 'Transcription' },
  { value: 'cpt_selection', label: 'CPT Selection' },
  { value: 'report_generation', label: 'Report Generation' },
  { value: 'intelligence_analytics', label: 'Intelligence / Analytics' },
  { value: 'ui_ux', label: 'UI / UX' },
  { value: 'other', label: 'Other' },
];

const SEVERITIES: { value: IssueSeverity; label: string; color: string }[] = [
  { value: 'critical', label: 'Critical — Blocking work', color: '#d32f2f' },
  { value: 'high', label: 'High — Major impact', color: '#ed6c02' },
  { value: 'medium', label: 'Medium — Workaround exists', color: '#fbc02d' },
  { value: 'low', label: 'Low — Minor / cosmetic', color: '#2e7d32' },
];

const ReportIssueWidget: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<IssueCategory>('transcription');
  const [severity, setSeverity] = useState<IssueSeverity>('medium');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [annotating, setAnnotating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [githubLinked, setGithubLinked] = useState(false);
  const [phiMaskingProgress, setPhiMaskingProgress] = useState<number | null>(null);
  const [phiMaskResult, setPhiMaskResult] = useState<MaskResult | null>(null);
  const [domMaskInfo, setDomMaskInfo] = useState<DomMaskResult['details'] | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const strokesRef = useRef<Array<Array<{ x: number; y: number }>>>([]);

  const handleOpen = useCallback(() => {
    setError(null);
    setSuccess(null);
    setOpen(true);
  }, []);

  // Keyboard shortcut: Ctrl+Shift+B (or Cmd+Shift+B on Mac) to open issue report
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'b' || e.key === 'B')) {
        e.preventDefault();
        handleOpen();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleOpen]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    setOpen(false);
    setAnnotating(false);
    setScreenshot(null);
    setDescription('');
    setCategory('transcription');
    setSeverity('medium');
    setPhiMaskResult(null);
    setPhiMaskingProgress(null);
    setDomMaskInfo(null);
    strokesRef.current = [];
  }, [submitting]);

  const captureScreenshot = useCallback(async () => {
    let domRestore: (() => void) | null = null;
    let dialogHidden = false;
    const dialogEl = document.querySelector('[data-report-issue-widget]');

    try {
      // ── Layer 1: DOM PHI masking (deterministic) ──
      // Mask all PHI text in the live DOM before capturing so the
      // screenshot never contains patient names, MRNs, DOBs, etc.
      const domResult = maskDomPhi();
      domRestore = domResult.restore;
      setDomMaskInfo(domResult.details);

      // Hide the report dialog so it doesn't appear in the screenshot
      if (dialogEl) {
        (dialogEl as HTMLElement).style.visibility = 'hidden';
        dialogHidden = true;
      }

      // Wait two frames for the browser to repaint with masked + hidden content
      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => requestAnimationFrame(r));

      // Use the browser's display media API
      const mediaStream = await navigator.mediaDevices.getDisplayMedia({
        video: { cursor: 'always' } as MediaTrackConstraints,
        audio: false,
      });

      const video = document.createElement('video');
      video.srcObject = mediaStream;
      await new Promise<void>((resolve) => {
        video.onloadedmetadata = () => {
          video.play();
          resolve();
        };
      });

      // Wait one frame to ensure video is rendered
      await new Promise((r) => requestAnimationFrame(r));

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not get canvas context');
      ctx.drawImage(video, 0, 0);

      // Stop the screen share stream immediately
      mediaStream.getTracks().forEach((t) => t.stop());

      // Restore the dialog visibility immediately after capture
      if (dialogHidden && dialogEl) {
        (dialogEl as HTMLElement).style.visibility = '';
        dialogHidden = false;
      }

      // Restore the DOM immediately after capture
      if (domRestore) {
        domRestore();
        domRestore = null;
      }

      // Convert to base64 PNG
      const rawDataUrl = canvas.toDataURL('image/png');

      // ── Layer 2: OCR PHI masking (secondary, catches PHI in images/canvas) ──
      setPhiMaskingProgress(0);
      try {
        const maskResult = await maskScreenshotPhi(rawDataUrl, (p) => {
          setPhiMaskingProgress(p);
        });
        setScreenshot(maskResult.dataUrl);
        setPhiMaskResult(maskResult);
        setAnnotating(true);
        strokesRef.current = [];
      } catch (maskErr) {
        // If OCR fails, the DOM masking (layer 1) already handled text-based PHI.
        // Only PHI embedded in images would be missed — warn the user.
        console.warn('OCR PHI masking failed, DOM masking still applied:', maskErr);
        setScreenshot(rawDataUrl);
        setAnnotating(true);
        strokesRef.current = [];
        if (domResult.maskedCount > 0) {
          setError('DOM PHI masking applied. OCR scan failed — if the screenshot contains PHI in images, please manually redact using the annotation tool.');
        }
      } finally {
        setPhiMaskingProgress(null);
      }
    } catch (err) {
      // User cancelled or permission denied
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setError('Screenshot permission was denied. You can still submit without a screenshot.');
      } else {
        setError('Could not capture screenshot. You can still submit without one.');
      }
    } finally {
      // Always restore dialog visibility and DOM, even if something failed mid-capture
      if (dialogHidden && dialogEl) {
        (dialogEl as HTMLElement).style.visibility = '';
      }
      if (domRestore) domRestore();
    }
  }, []);

  // ── Screenshot annotation ──────────────────────────────────────────────

  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Set canvas internal resolution to match the image's natural size
    if (canvas.width !== img.naturalWidth || canvas.height !== img.naturalHeight) {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
    }

    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // Draw strokes
    ctx.strokeStyle = '#ff1744';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    for (const stroke of strokesRef.current) {
      if (stroke.length < 2) continue;
      ctx.beginPath();
      ctx.moveTo(stroke[0].x, stroke[0].y);
      for (let i = 1; i < stroke.length; i++) {
        ctx.lineTo(stroke[i].x, stroke[i].y);
      }
      ctx.stroke();
    }
  }, []);

  const getCanvasPoint = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    drawingRef.current = true;
    lastPointRef.current = getCanvasPoint(e);
    strokesRef.current.push([lastPointRef.current]);
  }, [getCanvasPoint]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    const pt = getCanvasPoint(e);
    const currentStroke = strokesRef.current[strokesRef.current.length - 1];
    if (currentStroke) {
      currentStroke.push(pt);
    }
    redrawCanvas();
  }, [getCanvasPoint, redrawCanvas]);

  const handleMouseUp = useCallback(() => {
    drawingRef.current = false;
    lastPointRef.current = null;
  }, []);

  const handleUndoStroke = useCallback(() => {
    strokesRef.current.pop();
    redrawCanvas();
  }, [redrawCanvas]);

  const handleSaveAnnotation = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const annotated = canvas.toDataURL('image/png');
    setScreenshot(annotated);
    setAnnotating(false);
  }, []);

  const handleRemoveScreenshot = useCallback(() => {
    setScreenshot(null);
    setAnnotating(false);
    setPhiMaskResult(null);
    setDomMaskInfo(null);
    strokesRef.current = [];
  }, []);

  // ── Submit ─────────────────────────────────────────────────────────────

  const handleSubmit = useCallback(async () => {
    if (!description.trim()) {
      setError('Please describe what happened.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const evidence: EvidenceBundle = evidenceCollectorService.collect();

      const response = await issueReportService.submitIssueReport({
        category,
        severity,
        description: maskString(description.trim()),
        evidence,
        screenshot: screenshot ?? undefined,
        screenshotMimeType: 'image/png',
        correlationId: (window as unknown as { __correlationId?: string }).__correlationId,
      });

      evidenceCollectorService.clear();
      setGithubLinked(Boolean(response.githubIssueUrl));
      if (response.githubIssueUrl) {
        setSuccess(`Issue report #${response.id} submitted successfully. GitHub ticket #${response.githubIssueNumber} created. Thank you!`);
        setTimeout(() => {
          handleClose();
          setSuccess(null);
        }, 2000);
      } else {
        setSuccess(`Issue report #${response.id} saved, but no GitHub ticket was created. An administrator can check the integration and retry from Issue Reports. Do not submit this report again.`);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? `Failed to submit: ${err.message}`
          : 'Failed to submit issue report. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }, [category, severity, description, screenshot, handleClose]);

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <>
      <Tooltip title="Report an Issue (Ctrl+Shift+B)">
        <Fab
          color="secondary"
          size="medium"
          onClick={handleOpen}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 88,
            zIndex: 1200,
            bgcolor: 'warning.main',
            '&:hover': { bgcolor: 'warning.dark' },
          }}
        >
          <BugReportIcon />
        </Fab>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh' } }}
        data-report-issue-widget
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BugReportIcon color="warning" />
            <Typography variant="h6">Report an Issue</Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={submitting} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity={githubLinked ? 'success' : 'warning'} sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            {/* Category */}
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={category}
                label="Category"
                onChange={(e) => setCategory(e.target.value as IssueCategory)}
              >
                {CATEGORIES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Severity */}
            <FormControl fullWidth>
              <InputLabel>Severity</InputLabel>
              <Select
                value={severity}
                label="Severity"
                onChange={(e) => setSeverity(e.target.value as IssueSeverity)}
              >
                {SEVERITIES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color }} />
                      {s.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>How much does this impact your work?</FormHelperText>
            </FormControl>

            {/* Description */}
            <TextField
              label="What happened?"
              multiline
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what you were doing and what went wrong. e.g., 'I was dictating a CT abdomen report and the transcription stopped updating after about 30 seconds.'"
              fullWidth
              inputProps={{ maxLength: 5000 }}
              helperText={`${description.length}/5000 characters`}
            />

            {/* Screenshot */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Screenshot (optional)
              </Typography>

              {!screenshot && !annotating && phiMaskingProgress === null && (
                <Button
                  variant="outlined"
                  startIcon={<PhotoCameraIcon />}
                  onClick={captureScreenshot}
                  disabled={submitting}
                >
                  Capture Screenshot
                </Button>
              )}

              {phiMaskingProgress !== null && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                  <CircularProgress size={24} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Scanning screenshot for PHI...
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {Math.round(phiMaskingProgress * 100)}%
                    </Typography>
                  </Box>
                </Box>
              )}

              {annotating && screenshot && (
                <Box>
                  <Box sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                    <BrushIcon color="action" fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      Draw on the screenshot to highlight the issue
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'relative',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                      maxHeight: 400,
                      display: 'flex',
                      justifyContent: 'center',
                      bgcolor: 'background.default',
                    }}
                  >
                    <img
                      ref={imageRef}
                      src={screenshot}
                      alt="Screenshot to annotate"
                      style={{ display: 'none' }}
                      onLoad={redrawCanvas}
                    />
                    <canvas
                      ref={canvasRef}
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseUp}
                      style={{ maxWidth: '100%', maxHeight: 400, cursor: 'crosshair' }}
                    />
                  </Box>
                  <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                    <Button size="small" startIcon={<UndoIcon />} onClick={handleUndoStroke}>
                      Undo
                    </Button>
                    <Button size="small" variant="contained" onClick={handleSaveAnnotation}>
                      Save Annotation
                    </Button>
                    <Button size="small" color="error" onClick={handleRemoveScreenshot}>
                      Remove
                    </Button>
                  </Box>
                </Box>
              )}

              {screenshot && !annotating && (
                <Box>
                  {/* DOM masking info (layer 1) */}
                  {domMaskInfo && (domMaskInfo.textNodes + domMaskInfo.inputs + domMaskInfo.attributes + domMaskInfo.chips + domMaskInfo.labeled) > 0 && (
                    <Alert severity="success" sx={{ mb: 0.5, py: 0.5 }} icon={<ShieldIcon fontSize="small" />}>
                      <Typography variant="caption">
                        PHI masked: {domMaskInfo.labeled} labeled values, {domMaskInfo.tableColumns} table cells, {domMaskInfo.textNodes} text nodes, {domMaskInfo.inputs} inputs
                      </Typography>
                    </Alert>
                  )}
                  {/* OCR masking info (layer 2) */}
                  {phiMaskResult && phiMaskResult.maskedWords > 0 && (
                    <Alert severity="success" sx={{ mb: 0.5, py: 0.5 }} icon={<ShieldIcon fontSize="small" />}>
                      <Typography variant="caption">
                        OCR scan: {phiMaskResult.maskedWords} additional word{phiMaskResult.maskedWords !== 1 ? 's' : ''} redacted in images
                      </Typography>
                    </Alert>
                  )}
                  {domMaskInfo && phiMaskResult && phiMaskResult.maskedWords === 0 &&
                    (domMaskInfo.textNodes + domMaskInfo.inputs + domMaskInfo.attributes + domMaskInfo.chips + domMaskInfo.labeled) === 0 && (
                    <Alert severity="info" sx={{ mb: 0.5, py: 0.5 }} icon={<ShieldIcon fontSize="small" />}>
                      <Typography variant="caption">
                        No PHI detected in screenshot. Please verify no patient info is visible.
                      </Typography>
                    </Alert>
                  )}
                  <Box
                    component="img"
                    src={screenshot}
                    alt="Captured screenshot"
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 200,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                  <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                    <Button size="small" startIcon={<BrushIcon />} onClick={() => setAnnotating(true)}>
                      Annotate
                    </Button>
                    <Button size="small" color="error" onClick={handleRemoveScreenshot}>
                      Remove
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Evidence info */}
            <Alert severity="info" icon={false}>
              <Typography variant="caption" color="text.secondary">
                Console logs, network activity, and app state will be automatically collected
                and attached to this report. Screenshots use two-layer PHI protection:
                (1) DOM text is masked before capture, (2) OCR scans the image for any
                remaining PHI. All text-based evidence is also masked before submission.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={submitting}>
            {success ? 'Close' : 'Cancel'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={submitting ? <CircularProgress size={18} /> : <SendIcon />}
            onClick={handleSubmit}
            disabled={submitting || Boolean(success) || !description.trim()}
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReportIssueWidget;
